import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "./Button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"

export interface SortConfig {
  key: string
  direction: "asc" | "desc"
}

export interface Column<T> {
  key: keyof T
  label: string
  sortable?: boolean
  render?: (value: any, row: T) => React.ReactNode
  width?: string
}

export interface DataTableProps<T extends Record<string, any>> {
  columns: Column<T>[]
  data: T[]
  onSort?: (config: SortConfig) => void
  sortConfig?: SortConfig
  className?: string
  emptyMessage?: string
  onRowClick?: (row: T) => void
  isRowDisabled?: (row: T) => boolean
  rowTitle?: (row: T) => string | undefined
}

const DataTable = React.forwardRef<HTMLTableElement, DataTableProps<any>>(
  (
    {
      columns,
      data,
      onSort,
      sortConfig,
      className,
      emptyMessage = "No data available",
      onRowClick,
      isRowDisabled,
      rowTitle,
    },
    ref
  ) => {
    const handleSort = (key: string) => {
      if (!onSort) return
      const newDirection =
        sortConfig?.key === key && sortConfig?.direction === "asc"
          ? "desc"
          : "asc"
      onSort({ key, direction: newDirection })
    }

    return (
      <div className="w-full overflow-auto rounded-lg border border-border">
        <Table ref={ref} className={className}>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              {columns.map((column, colIdx) => (
                <TableHead
                  key={colIdx}
                  style={{ width: column.width }}
                  className={cn("px-4 py-3", {
                    "cursor-pointer select-none": column.sortable,
                  })}
                  onClick={() =>
                    column.sortable && handleSort(String(column.key))
                  }
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && (
                      <ChevronDown
                        className={cn("h-4 w-4 text-muted-foreground transition-transform", {
                          "rotate-180": sortConfig?.key === String(column.key) && sortConfig?.direction === "desc",
                          "opacity-0": sortConfig?.key !== String(column.key),
                        })}
                      />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-8 text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => {
                const disabled = isRowDisabled?.(row) ?? false
                return (
                <TableRow
                  key={idx}
                  onClick={onRowClick && !disabled ? () => onRowClick(row) : undefined}
                  title={rowTitle?.(row)}
                  className={cn({
                    "cursor-pointer": Boolean(onRowClick) && !disabled,
                    "opacity-60": disabled,
                  })}
                >
                  {columns.map((column, colIdx) => (
                    <TableCell
                      key={colIdx}
                      style={{ width: column.width }}
                      className="px-4 py-3"
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    )
  }
)
DataTable.displayName = "DataTable"

export { DataTable }
